import { useEffect, useState, type ReactNode } from 'react';
import type { PrivatePlayerSnapshot, PublicRoomSnapshot } from '@sincronia/shared';
import { COUNTDOWN_MS } from '@sincronia/shared';
import { Button, Overlay } from '../ui.js';
import { CardFace } from './PlayingCard.js';
import { PlayerAvatar } from '../Brand.js';
import { useT } from '../../i18n/index.js';
import { formatDuration, formatSeconds, joinNames } from '../../lib/format.js';
import { cn } from '../../lib/ui.js';
import { buildPlayerCards, type CardTone } from '../../lib/breakdown.js';
import { useGameStore } from '../../store/gameStore.js';

export type OverlayActions = {
  setReady: (ready: boolean) => void;
  voteStar: (approve: boolean) => void;
  playAgain: () => void;
  endGame: () => void;
  /** Sai da sala e volta para a tela inicial. */
  close: () => void;
};

function nameOf(snapshot: PublicRoomSnapshot, playerId: string): string {
  return snapshot.players.find((player) => player.id === playerId)?.name ?? '—';
}

function DiscardList({
  snapshot,
  groups,
}: {
  snapshot: PublicRoomSnapshot;
  groups: { playerId: string; values: number[] }[];
}): ReactNode {
  return (
    <ul className="flex flex-col gap-3" data-testid="discard-list">
      {groups.map((group) => {
        const player = snapshot.players.find((item) => item.id === group.playerId);
        return (
          <li key={group.playerId} className="flex items-center gap-3">
            {player ? (
              <PlayerAvatar avatarKey={player.avatarKey} name={player.name} size={26} />
            ) : null}
            <span className="w-24 truncate text-sm text-night-300">
              {nameOf(snapshot, group.playerId)}
            </span>
            <span className="flex flex-wrap gap-1">
              {group.values.map((value) => (
                <CardFace key={value} value={value} size="sm" dimmed />
              ))}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const CHIP_TONES: Record<CardTone, string> = {
  ok: 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200',
  erro: 'border-life/70 bg-life/15 text-life',
  perdida: 'border-night-600 bg-night-900/70 text-night-400 line-through',
} as const;

function ValueChip({ value, tone }: { value: number; tone: CardTone }): ReactNode {
  return (
    <span
      className={cn(
        'tabular rounded-md border px-1.5 py-0.5 text-xs font-semibold',
        CHIP_TONES[tone],
      )}
      data-tone={tone}
    >
      {value}
    </span>
  );
}

/**
 * Quem jogou o quê no nível. As cartas de cada pessoa aparecem sempre da menor
 * para a maior, com jogadas e descartadas na mesma sequência: verde é jogada na
 * ordem certa, vermelho é a carta que causou o erro e riscada é a carta perdida
 * (por erro de outra pessoa ou pela estrela).
 */
function LevelBreakdown({ snapshot }: { snapshot: PublicRoomSnapshot }): ReactNode {
  const t = useT();
  if (snapshot.pile.length === 0 && snapshot.discardedCards.length === 0) return null;

  return (
    <section className="mb-5 flex flex-col gap-2" data-testid="level-breakdown">
      <h3 className="text-xs font-medium tracking-wide text-night-400 uppercase">
        {t('level.byPlayer')}
      </h3>
      <ul className="flex flex-col gap-1.5">
        {snapshot.players.map((player) => {
          const cartas = buildPlayerCards(snapshot.pile, snapshot.discardedCards, player.id);
          return (
            <li
              key={player.id}
              className="flex items-center gap-2 rounded-xl bg-night-900/50 px-2 py-1.5"
              data-testid={`breakdown-${player.seat}`}
            >
              <PlayerAvatar avatarKey={player.avatarKey} name={player.name} size={22} />
              <span className="w-20 shrink-0 truncate text-sm text-night-300">{player.name}</span>
              <span className="flex flex-1 flex-wrap justify-end gap-1">
                {cartas.length === 0 ? (
                  <span className="text-xs text-night-500">{t('level.noCards')}</span>
                ) : (
                  cartas.map((carta) => (
                    <ValueChip key={carta.key} value={carta.value} tone={carta.tone} />
                  ))
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] leading-snug text-night-400">{t('level.legend')}</p>
    </section>
  );
}

function useCountdownSeconds(endsAt: number | null): number {
  const clockOffset = useGameStore((state) => state.clockOffset);
  const [remaining, setRemaining] = useState(() =>
    endsAt ? Math.ceil((endsAt - (Date.now() + clockOffset)) / 1000) : 0,
  );

  useEffect(() => {
    if (!endsAt) return;
    const update = (): void => {
      const now = Date.now() + useGameStore.getState().clockOffset;
      setRemaining(Math.max(0, Math.ceil((endsAt - now) / 1000)));
    };
    update();
    const handle = window.setInterval(update, 90);
    return () => window.clearInterval(handle);
  }, [endsAt]);

  return remaining;
}

function ReadyOverlay({
  snapshot,
  privateState,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const pending = snapshot.players.filter((player) => player.connected && !player.ready);
  const isReady = privateState?.ready ?? false;
  const title =
    snapshot.phase === 'REFOCUS' ? t('phase.REFOCUS') : t('ready.title', { level: snapshot.level });

  return (
    <Overlay open title={title} testId="overlay-ready">
      <p className="mb-4 text-sm text-night-300">{t('ready.body')}</p>

      {privateState && privateState.hand.length > 0 ? (
        <ul className="mb-5 flex flex-wrap justify-center gap-1.5">
          {privateState.hand.map((card) => (
            <li key={card.id}>
              <CardFace value={card.value} size="sm" />
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mb-4 min-h-5 text-center text-sm text-night-400" data-testid="ready-pending">
        {pending.length === 0
          ? t('ready.allReady')
          : t('ready.waiting', { names: joinNames(pending.map((player) => player.name)) })}
      </p>

      <Button
        variant={isReady ? 'secondary' : 'primary'}
        fullWidth
        onClick={() => actions.setReady(!isReady)}
        data-testid="ready-button"
      >
        {isReady ? t('common.loading') : t('ready.action')}
      </Button>
    </Overlay>
  );
}

function CountdownOverlay({ snapshot }: { snapshot: PublicRoomSnapshot }): ReactNode {
  const t = useT();
  const seconds = useCountdownSeconds(snapshot.countdownEndsAt);
  const label =
    seconds > 0 ? String(Math.min(seconds, Math.ceil(COUNTDOWN_MS / 1000))) : t('countdown.go');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-night-950/75 backdrop-blur-sm"
      role="status"
      aria-live="assertive"
      data-testid="overlay-countdown"
    >
      <span
        key={label}
        className="animate-pop bg-gradient-to-r from-pulse-400 to-nova-400 bg-clip-text text-[7rem] leading-none font-bold text-transparent"
      >
        {label}
      </span>
    </div>
  );
}

function StarVoteOverlay({
  snapshot,
  privateState,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const vote = snapshot.starVote;
  if (!vote) return null;

  const myId = privateState?.playerId ?? '';
  const alreadyVoted = vote.votes[myId] !== undefined;
  const pending = snapshot.players.filter(
    (player) => player.connected && vote.votes[player.id] === undefined,
  );

  return (
    <Overlay open title={t('star.title')} tone="star" testId="overlay-star-vote">
      <p className="mb-4 text-sm text-night-300">
        {t('star.body', { name: nameOf(snapshot, vote.requestedBy) })}
      </p>

      <p className="mb-4 text-center text-sm text-night-400" data-testid="star-pending">
        {pending.length === 0
          ? t('star.waiting')
          : t('star.pending', { names: joinNames(pending.map((player) => player.name)) })}
      </p>

      {alreadyVoted ? (
        <p className="text-center text-sm text-pulse-400">{t('star.waiting')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Button variant="primary" onClick={() => actions.voteStar(true)} data-testid="star-yes">
            {t('star.yes')}
          </Button>
          <Button variant="secondary" onClick={() => actions.voteStar(false)} data-testid="star-no">
            {t('star.no')}
          </Button>
        </div>
      )}
    </Overlay>
  );
}

function StarResultOverlay({
  snapshot,
  privateState,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const summary = snapshot.lastStar;
  const jaVi = privateState?.ready ?? false;
  const faltam = snapshot.players.filter((player) => player.connected && !player.ready);

  return (
    <Overlay open title={t('star.approvedTitle')} tone="star" testId="overlay-star-result">
      <p className="mb-4 text-sm text-night-300">{t('star.approvedBody')}</p>
      {summary ? <DiscardList snapshot={snapshot} groups={summary.discarded} /> : null}

      <p
        className="mt-5 mb-3 min-h-5 text-center text-sm text-night-400"
        data-testid="star-ack-pending"
      >
        {faltam.length === 0
          ? t('star.allSeen')
          : t('star.waitingSeen', { names: joinNames(faltam.map((player) => player.name)) })}
      </p>

      <Button
        variant={jaVi ? 'secondary' : 'primary'}
        fullWidth
        onClick={() => actions.setReady(!jaVi)}
        data-testid="star-seen"
      >
        {jaVi ? t('star.seenWaiting') : t('star.seen')}
      </Button>
    </Overlay>
  );
}

function ErrorOverlay({ snapshot }: { snapshot: PublicRoomSnapshot }): ReactNode {
  const t = useT();
  const summary = snapshot.lastError;
  if (!summary) return null;

  return (
    <Overlay open title={t('error.title')} tone="danger" testId="overlay-error">
      <div className="animate-shake mb-4 flex items-center gap-3">
        <CardFace value={summary.playedValue} size="md" />
        <p className="text-sm text-night-300">
          {t('error.body', {
            name: nameOf(snapshot, summary.playedBy),
            value: summary.playedValue,
          })}
          <br />
          <strong className="text-life">{t('error.lostLife')}</strong>
        </p>
      </div>
      <p className="mb-2 text-sm text-night-400">{t('error.discarded')}</p>
      <DiscardList snapshot={snapshot} groups={summary.discarded} />
    </Overlay>
  );
}

function LevelCompleteOverlay({
  snapshot,
  privateState,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const stats = snapshot.lastLevelSummary;
  const reward = snapshot.lastReward;
  const isReady = privateState?.ready ?? false;
  const pending = snapshot.players.filter((player) => player.connected && !player.ready);

  return (
    <Overlay
      open
      title={t('level.title', { level: stats?.level ?? snapshot.level })}
      tone="success"
      testId="overlay-level-complete"
    >
      {reward ? (
        <div className="animate-pop mb-4 rounded-2xl bg-star/10 p-4 text-center">
          <p className="text-lg font-semibold text-star">
            {reward.reward === 'star' ? t('reward.starTitle') : t('reward.lifeTitle')}
          </p>
          {reward.capped ? <p className="text-xs text-night-400">{t('reward.capped')}</p> : null}
        </div>
      ) : null}

      {stats ? (
        <dl className="mb-5 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-night-400">{t('level.cards')}</dt>
            <dd className="tabular text-xl text-night-200">{stats.cardsPlayed}</dd>
          </div>
          <div>
            <dt className="text-night-400">{t('level.errors')}</dt>
            <dd className="tabular text-xl text-night-200">{stats.errors}</dd>
          </div>
          <div>
            <dt className="text-night-400">{t('level.time')}</dt>
            <dd className="tabular text-xl text-night-200">{formatDuration(stats.totalMs)}</dd>
          </div>
          <div>
            <dt className="text-night-400">{t('level.average')}</dt>
            <dd className="tabular text-xl text-night-200">
              {formatSeconds(stats.averageIntervalMs)}
            </dd>
          </div>
        </dl>
      ) : null}

      <LevelBreakdown snapshot={snapshot} />

      <p className="mb-3 min-h-5 text-center text-sm text-night-400">
        {pending.length === 0
          ? t('ready.allReady')
          : t('ready.waiting', { names: joinNames(pending.map((player) => player.name)) })}
      </p>

      <Button
        variant={isReady ? 'secondary' : 'primary'}
        fullWidth
        onClick={() => actions.setReady(!isReady)}
        data-testid="level-continue"
      >
        {t('level.next', { level: snapshot.level + 1 })}
      </Button>
    </Overlay>
  );
}

function ConnectionPausedOverlay({
  snapshot,
  isHost,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  isHost: boolean;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const offline = snapshot.players.filter((player) => !player.connected);

  return (
    <Overlay open title={t('paused.title')} tone="danger" testId="overlay-paused">
      <p className="mb-4 text-sm text-night-300">
        {offline.length === 1 && offline[0]
          ? t('paused.body', { name: offline[0].name })
          : t('paused.bodyGeneric')}
      </p>
      <p className="mb-4 text-center text-sm text-night-400">{t('paused.deadline')}</p>

      {isHost ? (
        <>
          <Button
            variant="danger"
            fullWidth
            disabled={!snapshot.canEndGame}
            onClick={actions.endGame}
            data-testid="end-game"
          >
            {t('paused.end')}
          </Button>
          {!snapshot.canEndGame ? (
            <p className="mt-2 text-center text-xs text-night-400">{t('paused.endHint')}</p>
          ) : null}
        </>
      ) : null}
    </Overlay>
  );
}

function GameOverOverlay({
  snapshot,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();
  const won = snapshot.phase === 'GAME_WON';
  const stats = snapshot.gameStats;

  return (
    <Overlay
      open
      title={won ? t('won.title') : t('lost.title')}
      tone={won ? 'success' : 'danger'}
      testId={won ? 'overlay-won' : 'overlay-lost'}
    >
      <p className="mb-5 text-sm text-night-300">
        {won
          ? t('won.body', { levels: snapshot.maxLevel })
          : t('lost.body', { level: snapshot.level })}
      </p>

      <dl className="mb-6 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-night-400">{t('won.levels')}</dt>
          <dd className="tabular text-xl text-night-200">{stats.levelsCompleted}</dd>
        </div>
        <div>
          <dt className="text-night-400">{t('won.cards')}</dt>
          <dd className="tabular text-xl text-night-200">{stats.cardsPlayed}</dd>
        </div>
        <div>
          <dt className="text-night-400">{t('won.errors')}</dt>
          <dd className="tabular text-xl text-night-200">{stats.errors}</dd>
        </div>
        <div>
          <dt className="text-night-400">{t('won.stars')}</dt>
          <dd className="tabular text-xl text-night-200">{stats.starsUsed}</dd>
        </div>
      </dl>

      <LevelBreakdown snapshot={snapshot} />

      <div className="flex flex-col gap-2">
        <Button variant="primary" fullWidth onClick={actions.playAgain} data-testid="play-again">
          {won ? t('won.again') : t('lost.back')}
        </Button>
        <Button variant="ghost" fullWidth onClick={actions.close} data-testid="close-game">
          {t('common.close')}
        </Button>
      </div>
    </Overlay>
  );
}

export function Overlays({
  snapshot,
  privateState,
  isHost,
  actions,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  isHost: boolean;
  actions: OverlayActions;
}): ReactNode {
  const t = useT();

  switch (snapshot.phase) {
    case 'READY':
    case 'REFOCUS':
      return <ReadyOverlay snapshot={snapshot} privateState={privateState} actions={actions} />;
    case 'COUNTDOWN':
      return <CountdownOverlay snapshot={snapshot} />;
    case 'STAR_VOTE':
      return <StarVoteOverlay snapshot={snapshot} privateState={privateState} actions={actions} />;
    case 'RESOLVING_STAR':
      return (
        <StarResultOverlay snapshot={snapshot} privateState={privateState} actions={actions} />
      );
    case 'RESOLVING_ERROR':
      return <ErrorOverlay snapshot={snapshot} />;
    case 'LEVEL_COMPLETE':
      return (
        <LevelCompleteOverlay snapshot={snapshot} privateState={privateState} actions={actions} />
      );
    case 'CONNECTION_PAUSED':
      return <ConnectionPausedOverlay snapshot={snapshot} isHost={isHost} actions={actions} />;
    case 'GAME_WON':
    case 'GAME_LOST':
      return <GameOverOverlay snapshot={snapshot} actions={actions} />;
    case 'DEALING':
      return (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-night-950/60 backdrop-blur-sm"
          role="status"
          data-testid="overlay-dealing"
        >
          <p className="animate-pulse text-lg text-night-300">{t('phase.DEALING')}</p>
        </div>
      );
    default:
      return null;
  }
}
